/**
 * The passkey signatures on an access request, as the member views render
 * them (`signatures: [{role, approvalStage, signedAt, signerName}]`), and the
 * words the Signed badge's tooltip uses for each.
 *
 * Absent on a server that predates signed approvals, and null where a view
 * did not load them: every reader treats a missing, null or malformed field
 * as "no signatures" and shows nothing.
 */
import { format } from "date-fns";
import type { MFSignature } from "state/members/stepUp";

/** The well-formed signatures in whatever the server sent, oldest first. */
export const readSignatures = (value: unknown): MFSignature[] => {
  if (!Array.isArray(value)) return [];
  return value
    .filter(
      (s): s is MFSignature =>
        Boolean(s) &&
        typeof s === "object" &&
        typeof (s as MFSignature).role === "string" &&
        typeof (s as MFSignature).signedAt === "string",
    )
    .sort((a, b) => Date.parse(a.signedAt) - Date.parse(b.signedAt));
};

const roleLabel = (s: MFSignature): string => {
  if (s.role === "requester") return "as the requester";
  if (s.role === "admin_override") return "as an admin";
  if (s.role === "approver") {
    return typeof s.approvalStage === "number" && s.approvalStage >= 0
      ? `as approver, step ${s.approvalStage + 1}`
      : "as approver";
  }
  return "";
};

const whenLabel = (iso: string): string => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? "at an unknown time"
    : `on ${format(date, "d MMM yyyy 'at' HH:mm")}`;
};

/** "Alice Smith signed as approver, step 2, on 3 Oct 2026 at 14:02". */
export const signatureLine = (s: MFSignature): string => {
  const who = s.signerName?.trim() || "Someone";
  const role = roleLabel(s);
  return `${who} signed${role ? ` ${role},` : ""} ${whenLabel(s.signedAt)}`;
};

/** Whether the signer has since reported this passkey as not theirs. */
export const isFlagged = (s: MFSignature): boolean =>
  typeof s.flaggedForReviewAt === "string" && s.flaggedForReviewAt.trim() !== "";

export const FLAGGED_SUMMARY =
  "The member reported that they did not add this passkey. An admin should review this request.";

export type SignatureBadgeModel = {
  flagged: boolean;
  label: "Signed" | "Flagged for review";
  /** The tooltip, one line each. */
  lines: string[];
};

/**
 * What the badge shows for whatever the server sent: nothing for a missing,
 * null or empty field; "Flagged for review" when any signature was reported;
 * otherwise "Signed".
 */
export const signatureBadge = (value: unknown): SignatureBadgeModel | null => {
  const list = readSignatures(value);
  if (list.length === 0) return null;
  const flagged = list.some(isFlagged);
  const lines = list.map((s) =>
    isFlagged(s) ? `${signatureLine(s)} (flagged for review)` : signatureLine(s),
  );
  return flagged
    ? { flagged: true, label: "Flagged for review", lines: [FLAGGED_SUMMARY, ...lines] }
    : { flagged: false, label: "Signed", lines };
};
